import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MONTHS_EL_SHORT } from "@/lib/format";
import { PctInput } from "@/components/UnitModeSelect";

const MK = Array.from({ length: 12 }, (_, i) => String(i + 1));
const inp = "mt-1 w-full rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";
const lbl = "text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold";

const fromLine = (l) => {
  const worked = MK.filter((m) => Number(l?.months?.[m]) > 0);
  return {
    source: "new", employeeId: "", name: l?.name || "", code: l?.code ?? null,
    department: l?.department || "", position: l?.position || "",
    salary: worked.reduce((mx, m) => Math.max(mx, Number(l.months[m]) || 0), 0) || "",
    months: worked.length ? worked : ["5", "6", "7", "8", "9", "10"],
    has_housing: l?.has_housing ?? true, has_feeding: l?.has_feeding ?? true,
    taf: Math.round((l?.taf_pct ?? 1) * 100),
  };
};

const Toggle = ({ testid, checked, onChange, label }) => (
  <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
    className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${checked ? "bg-[#6E7A63] text-white border-[#6E7A63]" : "bg-white text-[#6F6A60] border-[#E3DED4] hover:border-[#B9AE9A]"}`}>
    {label}: {checked ? "Ναι" : "Όχι"}
  </button>
);

export function BudgetLineDialog({ open, onOpenChange, line, employees, departments, roles, rate, onSubmit }) {
  const [f, setF] = useState(fromLine(null));
  const editing = !!line;
  useEffect(() => { if (open) { setF(fromLine(line)); } }, [open, line]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const pickEmployee = (id) => {
    const e = employees.find((x) => x.id === id);
    if (!e) { set("employeeId", ""); return; }
    const wm = (e.work_months || []).map(String);
    setF((p) => ({ ...p, employeeId: id, name: e.name, code: e.code ?? null,
      department: e.department || "", position: e.position || "",
      salary: Number(e.monthly_base) || p.salary, months: wm.length ? wm : p.months,
      taf: e.taf_pct != null ? Math.round(e.taf_pct * 100) : p.taf }));
  };

  const toggleMonth = (m) => set("months", f.months.includes(m) ? f.months.filter((x) => x !== m) : [...f.months, m]);

  const submit = () => {
    const salary = Number(f.salary) || 0;
    const orig = line?.months || {};
    const origSalary = fromLine(line).salary;
    const months = {};
    f.months.forEach((m) => { months[m] = editing && Number(orig[m]) > 0 && salary === origSalary ? Number(orig[m]) : salary; });
    onSubmit({ ...(line || { easter: 0, leave: 0, christmas: 0, ika: 0, fmy: 0 }),
      code: f.code, name: f.name.trim(), department: f.department.trim(), position: f.position.trim(),
      months, has_housing: f.has_housing, has_feeding: f.has_feeding,
      housing_override: f.has_housing === (line?.has_housing ?? true) ? (line?.housing_override ?? null) : null,
      feeding_override: f.has_feeding === (line?.has_feeding ?? true) ? (line?.feeding_override ?? null) : null,
      taf_pct: Math.min(100, Math.max(0, Number(f.taf) || 0)) / 100 });
  };

  const valid = f.name.trim() && Number(f.salary) > 0 && f.months.length > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="budget-line-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Επεξεργασία θέσης" : "Προσθήκη θέσης / υπαλλήλου"}</DialogTitle>
          <DialogDescription>Μπαίνει μόνο στον προϋπολογισμό. Πατήστε Αποθήκευση στη σελίδα για να καταχωρηθεί.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!editing && (
            <div className="flex rounded-xl border border-[#E3DED4] p-1" data-testid="budget-line-source">
              {[["new", "Νέα θέση"], ["employee", "Υπάρχων υπάλληλος"]].map(([k, t]) => (
                <button key={k} type="button" data-testid={`budget-line-source-${k}`} onClick={() => setF({ ...fromLine(null), source: k })}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold ${f.source === k ? "bg-[#45423C] text-white" : "text-[#6F6A60]"}`}>{t}</button>
              ))}
            </div>
          )}
          {!editing && f.source === "employee" ? (
            <label className="block"><span className={lbl}>Υπάλληλος</span>
              <select data-testid="budget-line-employee" value={f.employeeId} onChange={(e) => pickEmployee(e.target.value)} className={inp}>
                <option value="">Επιλέξτε…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.position ? ` · ${e.position}` : ""}</option>)}
              </select>
            </label>
          ) : (
            <label className="block"><span className={lbl}>Όνομα ή τύπος θέσης</span>
              <input data-testid="budget-line-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="π.χ. Σερβιτόρος #2" className={inp} />
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={lbl}>Τμήμα</span>
              <select data-testid="budget-line-department" value={f.department} onChange={(e) => set("department", e.target.value)} className={inp}>
                <option value="">— επιλέξτε —</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                {f.department && !departments.includes(f.department) && <option value={f.department}>{f.department} (εκτός λίστας)</option>}
              </select>
            </label>
            <label className="block"><span className={lbl}>Ρόλος</span>
              <select data-testid="budget-line-position" value={f.position} onChange={(e) => set("position", e.target.value)} className={inp}>
                <option value="">— επιλέξτε —</option>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                {f.position && !roles.includes(f.position) && <option value={f.position}>{f.position} (εκτός λίστας)</option>}
              </select>
            </label>
            <label className="block"><span className={lbl}>Μηνιαίος μισθός (€)</span>
              <input data-testid="budget-line-salary" type="number" min="0" value={f.salary} onChange={(e) => set("salary", e.target.value)} className={inp} />
            </label>
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <label className="block"><span className={lbl}>TAF %</span>
                <PctInput testid="budget-line-taf" pct={(Number(f.taf) || 0) / 100} onChange={(v) => set("taf", Math.round(v * 100))} className={inp} />
              </label>
              <label className="block"><span className={lbl}>TRIBE %</span>
                <PctInput testid="budget-line-tribe" pct={(100 - (Number(f.taf) || 0)) / 100} onChange={(v) => set("taf", 100 - Math.round(v * 100))} className={inp} />
              </label>
            </div>
          </div>
          <div>
            <span className={lbl}>Μήνες εργασίας</span>
            <div className="mt-1 grid grid-cols-6 gap-1.5">
              {MK.map((m) => (
                <button key={m} type="button" data-testid={`budget-line-month-${m}`} onClick={() => toggleMonth(m)}
                  className={`rounded-lg border py-1.5 text-xs font-semibold ${f.months.includes(m) ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4]"}`}>{MONTHS_EL_SHORT[Number(m)]}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Toggle testid="budget-line-housing" checked={f.has_housing} onChange={(v) => set("has_housing", v)} label="Στέγαση" />
            <Toggle testid="budget-line-feeding" checked={f.has_feeding} onChange={(v) => set("has_feeding", v)} label="Σίτιση" />
          </div>
          <p className="text-xs text-[#6F6A60]">Στέγαση {rate?.housing ?? 100} € και Σίτιση {rate?.feeding ?? 100} € ανά μήνα εργασίας. Δώρα και ΙΚΑ υπολογίζονται αυτόματα.</p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" data-testid="budget-line-cancel" onClick={() => onOpenChange(false)} className="rounded-xl px-4 py-2.5 text-sm text-[#6F6A60]">Άκυρο</button>
            <button type="button" data-testid="budget-line-submit" disabled={!valid} onClick={submit}
              className="rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50">{editing ? "Ενημέρωση" : "Προσθήκη"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
